import { useEffect, useRef } from 'react'
import { Package, ArrowRight } from 'lucide-react'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { myAccountRequestsPath } from 'src/routes/paths'
import { Column } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { EmptyState } from 'src/shared/components/EmptyState'
import {
  DELIVERED_STATUS,
  STARTED_PRODUCTION_STATUS,
  SHIPPING_QUOTE_STATUS,
  effectiveQuoteStatus,
} from 'src/shared/consts/quoteStatus'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useRequestContext } from '../../../hooks/RequestContext'
import { useOrderViewState } from '../../../hooks/useOrderViewState'
import { BillingDocument } from '../../BillingDocument'
import {
  buildIssuedInvoiceData,
  getBillingDocumentData,
} from '../../BillingDocument/utils'
import { RequestTabShell } from '../../RequestTabShell'
import { DeliveryTrackingHero } from './components/DeliveryTrackingHero'

const isDeliveryUnlocked = (status: string) =>
  status === STARTED_PRODUCTION_STATUS ||
  status === SHIPPING_QUOTE_STATUS ||
  status === DELIVERED_STATUS

export const SummaryTab = () => {
  const { t } = useLocalization()
  const { publicId, quote, quoteItems, refetchQuote, isDemo, demoShipment } =
    useRequestContext()
  const { setTab } = useOrderViewState(myAccountRequestsPath)
  const { updateQuote } = useQuoteApi({ skipListQuery: true })
  const status = effectiveQuoteStatus(quote?.status)
  const issuedInvoiceForQuoteRef = useRef<number | null>(null)

  // Delivery treats the invoice as issued. Persist seeded invoice_data once
  // for orders that reached production before this was automatic.
  useEffect(() => {
    if (isDemo) return
    if (!quote || !isDeliveryUnlocked(status)) return
    if (issuedInvoiceForQuoteRef.current === quote.id) return

    const current = getBillingDocumentData(quote)
    if (!current.isDraftPreview) {
      issuedInvoiceForQuoteRef.current = quote.id
      return
    }

    issuedInvoiceForQuoteRef.current = quote.id
    void updateQuote
      .mutateAsync({
        id: quote.id,
        invoice_data: buildIssuedInvoiceData(quote),
      })
      .then((result) => {
        if (result.error) throw result.error
        refetchQuote()
      })
      .catch((error) => {
        console.error('Failed to issue invoice on delivery:', error)
        issuedInvoiceForQuoteRef.current = null
      })
  }, [isDemo, quote, refetchQuote, status, updateQuote])

  return (
    <RequestTabShell
      loadingMessage={t('Loading delivery...', 'Laddar leverans...')}
    >
      {quote && !isDeliveryUnlocked(status) ? (
        <EmptyState
          icon={<Package className="h-9 w-9" />}
          title={t(
            'Delivery unlocks after production starts',
            'Leverans öppnas när produktionen startar'
          )}
          description={t(
            'Finish Checkout first: approve the quote, approve factory proofs, then confirm shipping and billing.',
            'Slutför Kassa först: godkänn offerten, godkänn korrekturer och bekräfta sedan leverans och faktura.'
          )}
          button={
            <Button variant="primary" onClick={() => setTab('checkout')}>
              {t('Go to checkout', 'Gå till kassan')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          }
        />
      ) : null}

      {quote && isDeliveryUnlocked(status) ? (
        <Column className="w-full items-center gap-6 pb-8">
          <Column className="w-full max-w-4xl gap-1">
            <h3>{t('Delivery', 'Leverans')}</h3>
            <p>
              {t(
                'Track your shipment here. Invoice details are below.',
                'Spåra din leverans här. Fakturauppgifter finns nedan.'
              )}
            </p>
          </Column>
          <DeliveryTrackingHero
            quoteId={quote.id}
            quotePublicId={publicId ?? quote.public_id}
            quoteStatus={quote.status}
            demoShipment={demoShipment}
          />
          <BillingDocument
            quote={quote}
            quoteItems={quoteItems}
            publicId={publicId}
            mode="invoice"
            isReadOnly={isDemo}
          />
        </Column>
      ) : null}
    </RequestTabShell>
  )
}
